import React, { useState, useEffect } from 'react';
import './liveClassWorkShop.css';

const LiveClassWorkshop = () => {
  const [activeTab, setActiveTab] = useState('liveClass');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    googleMeet: '',
    classDate: ''
  });
  const [liveClasses, setLiveClasses] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = activeTab === 'liveClass' 
        ? 'https://admindashboard.clns.in/api/student/live-classes/'
        : 'https://admindashboard.clns.in/api/student/workshop_event/';
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      let processedData = [];
      if (data && typeof data === 'object') {
        if (Array.isArray(data)) {
          processedData = data;
        } else if (data.results && Array.isArray(data.results)) {
          processedData = data.results;
        } else if (data.data && Array.isArray(data.data)) {
          processedData = data.data;
        }
      }

      if (activeTab === 'liveClass') {
        setLiveClasses(processedData);
      } else {
        setWorkshops(processedData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateTimeChange = (e) => {
    try {
      const localDateTime = e.target.value;
      if (!localDateTime) {
        setFormData(prev => ({ ...prev, classDate: '' }));
        return;
      }
      
      // Convert the local datetime input back to ISO string (UTC)
      const isoDateTime = new Date(localDateTime).toISOString();
      setFormData(prev => ({
        ...prev,
        classDate: isoDateTime
      }));
    } catch (error) {
      console.error('Date conversion error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title?.trim() || !formData.description?.trim() || 
        !formData.googleMeet?.trim() || !formData.classDate) {
      alert('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      const url = activeTab === 'liveClass'
        ? 'https://admindashboard.clns.in/api/student/live-classes/'
        : 'https://admindashboard.clns.in/api/student/workshop_event/';

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to add item');
      }

      await fetchData();

      setFormData({
        title: '',
        description: '',
        googleMeet: '',
        classDate: ''
      });

    } catch (error) {
      console.error('Error adding item:', error);
      alert(error.message || 'Failed to add item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;

    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    setLoading(true);
    try {
      const url = activeTab === 'liveClass'
        ? `https://admindashboard.clns.in/api/student/live-classes/${id}/`
        : `https://admindashboard.clns.in/api/student/workshop_event/${id}/`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      // Remove the deleted item from the appropriate state (LiveClasses or Workshops)
      if (activeTab === 'liveClass') {
        setLiveClasses(prevClasses => prevClasses.filter(item => item.id !== id));
      } else {
        setWorkshops(prevWorkshops => prevWorkshops.filter(item => item.id !== id));
      }

    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Format the date using local time
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,  // Optional: Enabling 12-hour format with AM/PM
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  const renderLiveClassesTable = () => (
    <table>
      <thead>
        <tr>
          <th>Title</th>
          <th>Description</th>
          <th>Date & Time</th>
          <th>Google Meet Link</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {liveClasses.map(item => (
          <tr key={item.id || Date.now()}>
            <td>{item?.title || 'N/A'}</td>
            <td>{item?.description || 'N/A'}</td>
            <td>{formatDateTime(item?.classDate)}</td>
            <td>{item?.googleMeet || 'N/A'}</td>
            <td>
              <button
                onClick={() => handleDelete(item.id)}
                className="delete-button"
                disabled={loading}
              >
                {loading ? '...' : 'Delete'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderWorkshopsTable = () => (
    <table>
      <thead>
        <tr>
          <th>Workshop Title</th>
          <th>Description</th>
          <th>Event Date</th>
          <th>Meeting Link</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {workshops.map(item => (
          <tr key={item.id || Date.now()}>
            <td>{item?.title || 'N/A'}</td>
            <td>{item?.description || 'N/A'}</td>
            <td>{formatDateTime(item?.classDate)}</td>
            <td>{item?.googleMeet || 'N/A'}</td>
            <td>
              <button
                onClick={() => handleDelete(item.id)}
                className="delete-button"
                disabled={loading}
              >
                {loading ? '...' : 'Delete'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="container">
      <div className="tab-container">
        <button
          onClick={() => setActiveTab('liveClass')}
          className={`tab ${activeTab === 'liveClass' ? 'active' : ''}`}
        >
          Live Class
        </button>
        <button
          onClick={() => setActiveTab('workshop')}
          className={`tab ${activeTab === 'workshop' ? 'active' : ''}`}
        >
          Workshops
        </button>
      </div>

      <form onSubmit={handleSubmit} className="form">
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          placeholder="Title"
          className="input-field"
          disabled={loading}
        />
        <input
          type="text"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Description"
          className="input-field"
          disabled={loading}
        />
        <input
          type="datetime-local"
          name="classDate"
          value={formData.classDate ? new Date(formData.classDate).toLocaleString('sv-SE').slice(0, 16) : ''}
          onChange={handleDateTimeChange}
          className="input-field"
          disabled={loading}
        />
        <input
          type="text"
          name="googleMeet"
          value={formData.googleMeet}
          onChange={handleInputChange}
          placeholder="Google meet/link"
          className="input-field"
          disabled={loading}
        />
        <button 
          type="submit" 
          className="add-button"
          disabled={loading}
        >
          {loading ? 'Adding...' : '+ Add'}
        </button>
      </form>

      <div className="table-container">
        {loading ? (
          <p className="loading-message">Loading...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : activeTab === 'liveClass' ? (
          liveClasses.length === 0 ? (
            <p className="no-data-message">No live classes found.</p>
          ) : (
            renderLiveClassesTable()
          )
        ) : (
          workshops.length === 0 ? (
            <p className="no-data-message">No workshops found.</p>
          ) : (
            renderWorkshopsTable()
          )
        )}
      </div>
    </div>
  );
};

export default LiveClassWorkshop;
